/**
 * Kpop Spotlight — the front page's queue: choosing a pick shows it on the
 * stage. Only the front page does this; everywhere else a card opens the pick.
 *
 * The server rendered every queue pick's stage into a <template>, so this only
 * swaps markup in. Where the browser has view transitions the frame and copy
 * morph between picks; otherwise the swap is instant. Without JS the queue
 * rows are links to each pick's own page.
 */

import { initStillFallbacks, isPlainClick, reducedMotion } from './player.js';
import { isBusy, teardown } from './stage.js';

function morph(change) {
	if (reducedMotion() || !document.startViewTransition) {
		change();
		return Promise.resolve();
	}
	return document.startViewTransition(change).finished.catch(() => {});
}

function initQueue(section) {
	const holder = section.querySelector('[data-spot-front]');
	const queue = section.querySelector('[data-spot-queue]');
	const status = section.querySelector('[data-spot-status]');
	if (!holder || !queue) return;

	const templates = new Map([...section.querySelectorAll('template[data-spot-tpl]')].map((t) => [Number(t.dataset.spotTpl), t]));
	let current = 0;
	let swapping = false;

	function show(index) {
		const stage = holder.querySelector('[data-spot-stage]');
		const tpl = templates.get(index);
		// One swap at a time: a second view transition would skip the first
		// while its callback still ran, leaving stage and queue out of step.
		if (swapping || !tpl || !stage || isBusy(stage)) return Promise.resolve();
		if (index === current && !stage.classList.contains('is-playing')) return Promise.resolve();

		swapping = true;
		teardown(stage);
		const next = tpl.content.firstElementChild.cloneNode(true);
		current = index;
		queue.querySelectorAll('[data-spot-q]').forEach((q) => q.setAttribute('aria-current', Number(q.dataset.spotQ) === index ? 'true' : 'false'));
		if (status) status.textContent = `Spotlightissa: ${next.querySelector('.spot-stage__name')?.textContent.trim() || ''}`;

		return morph(() => {
			// Looked up again here: the callback runs later than show().
			holder.querySelector('[data-spot-stage]')?.replaceWith(next);
			initStillFallbacks(next);
		}).finally(() => {
			swapping = false;
		});
	}

	queue.addEventListener('click', (e) => {
		const item = e.target.closest('[data-spot-q]');
		if (!item || !isPlainClick(e)) return;
		e.preventDefault();
		show(Number(item.dataset.spotQ));
	});

	holder.addEventListener('click', (e) => {
		if (!e.target.closest('[data-spot-back]')) return;
		// The button that was clicked is gone after the swap, so hand focus to the title.
		show(0).then(() => holder.querySelector('.spot-stage__name')?.focus({ preventScroll: true }));
	});
}

export function initQueues() {
	document.querySelectorAll('.spot-front').forEach(initQueue);
}
